import * as React from 'react';
import { useState, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { Data, Layout } from 'plotly.js';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import { plotlyLayout, plotlyConfig } from '../theme/plotlyTheme';
import { colors } from '../theme/colors';
import { PlotControlsToolbar } from './PlotControlsToolbar';
import { PlotFullscreenModal } from './PlotFullscreenModal';

interface PlotData {
  measured_value: string;
  timestamps: string[][];
  mean: number[][];
  median: number[][];
  stddev: number[][];
  q1: number[][];
  q3: number[][];
  max: number[][];
  min: number[][];
  point_names: string[];
  shown_names: string[];
}

interface PlotDataProps {
  plot_data: PlotData;
}

export const BoxPlot: React.FC<PlotDataProps> = ({ plot_data }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gridVisible, setGridVisible] = useState(true);
  const [legendVisible, setLegendVisible] = useState(true);
  const [layout, setLayout] = useState<Partial<Layout>>({
    ...plotlyLayout,
    xaxis: { ...plotlyLayout.xaxis, title: 'Время' },
    yaxis: { ...plotlyLayout.yaxis, title: plot_data.measured_value },
    boxmode: 'group',
  });

  const data_for_plot: Data[] = [];

  for (let i = 0; i < plot_data.point_names.length; i++) {
    const timestamps = [];
    for (const item of plot_data.timestamps[i]) {
      const timestamp = new Date(item);
      timestamps.push(timestamp);
    }

    data_for_plot.push({
      x: timestamps,
      // @ts-ignore
      median: plot_data.median[i],
      stddev: plot_data.stddev[i],
      q1: plot_data.q1[i],
      q3: plot_data.q3[i],
      upperfence: plot_data.max[i],
      lowerfence: plot_data.min[i],
      name: plot_data.shown_names[i],
      type: 'box',
    });
  }

  const handleRelayout = useCallback((newLayout: any) => {
    setLayout(prevLayout => {
      const updates: any = { ...prevLayout };

      // Handle x-axis range changes
      if ('xaxis.range[0]' in newLayout && 'xaxis.range[1]' in newLayout) {
        updates.xaxis = {
          ...prevLayout.xaxis,
          range: [newLayout['xaxis.range[0]'], newLayout['xaxis.range[1]']],
          autorange: false,
        };
      } else if ('xaxis.range' in newLayout) {
        updates.xaxis = {
          ...prevLayout.xaxis,
          range: newLayout['xaxis.range'],
          autorange: false,
        };
      } else if ('xaxis.autorange' in newLayout) {
        updates.xaxis = {
          ...prevLayout.xaxis,
          autorange: newLayout['xaxis.autorange'],
          range: undefined,
        };
      }

      // Handle y-axis range changes
      if ('yaxis.range[0]' in newLayout && 'yaxis.range[1]' in newLayout) {
        updates.yaxis = {
          ...prevLayout.yaxis,
          range: [newLayout['yaxis.range[0]'], newLayout['yaxis.range[1]']],
          autorange: false,
        };
      } else if ('yaxis.range' in newLayout) {
        updates.yaxis = {
          ...prevLayout.yaxis,
          range: newLayout['yaxis.range'],
          autorange: false,
        };
      } else if ('yaxis.autorange' in newLayout) {
        updates.yaxis = {
          ...prevLayout.yaxis,
          autorange: newLayout['yaxis.autorange'],
          range: undefined,
        };
      }

      return updates;
    });
  }, []);

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  const handleResetZoom = () => {
    setLayout((prevLayout) => ({
      ...prevLayout,
      xaxis: {
        ...prevLayout.xaxis,
        autorange: true,
        range: undefined,
      },
      yaxis: {
        ...prevLayout.yaxis,
        autorange: true,
        range: undefined,
      },
    }));
  };

  const handleToggleGrid = () => {
    setGridVisible((prev) => !prev);
  };

  const handleToggleLegend = () => {
    setLegendVisible((prev) => !prev);
  };

  const handleCopyData = () => {
    // Convert plot data to CSV string
    const headers = ['timestamp'];
    for (const name of plot_data.point_names) {
      headers.push(`${name}_median`, `${name}_q1`, `${name}_q3`, `${name}_min`, `${name}_max`);
    }
    const rows: string[] = [headers.join(',')];

    // Prepare data rows - align by timestamp
    const maxLength = Math.max(...plot_data.timestamps.map((t) => t.length));
    for (let i = 0; i < maxLength; i++) {
      const row: string[] = [];
      // Use the first timestamp array as reference
      if (plot_data.timestamps[0] && plot_data.timestamps[0][i]) {
        row.push(plot_data.timestamps[0][i]);
      } else {
        row.push('');
      }
      // Add values for each series
      for (let j = 0; j < plot_data.point_names.length; j++) {
        row.push(plot_data.median[j][i]?.toString() || '');
        row.push(plot_data.q1[j][i]?.toString() || '');
        row.push(plot_data.q3[j][i]?.toString() || '');
        row.push(plot_data.min[j][i]?.toString() || '');
        row.push(plot_data.max[j][i]?.toString() || '');
      }
      rows.push(row.join(','));
    }

    const csvContent = rows.join('\n');
    navigator.clipboard.writeText(csvContent);
  };

  const handleDownloadCSV = () => {
    // Convert plot data to CSV string
    const headers = ['timestamp'];
    for (const name of plot_data.point_names) {
      headers.push(`${name}_median`, `${name}_q1`, `${name}_q3`, `${name}_min`, `${name}_max`);
    }
    const rows: string[] = [headers.join(',')];

    // Prepare data rows - align by timestamp
    const maxLength = Math.max(...plot_data.timestamps.map((t) => t.length));
    for (let i = 0; i < maxLength; i++) {
      const row: string[] = [];
      // Use the first timestamp array as reference
      if (plot_data.timestamps[0] && plot_data.timestamps[0][i]) {
        row.push(plot_data.timestamps[0][i]);
      } else {
        row.push('');
      }
      // Add values for each series
      for (let j = 0; j < plot_data.point_names.length; j++) {
        row.push(plot_data.median[j][i]?.toString() || '');
        row.push(plot_data.q1[j][i]?.toString() || '');
        row.push(plot_data.q3[j][i]?.toString() || '');
        row.push(plot_data.min[j][i]?.toString() || '');
        row.push(plot_data.max[j][i]?.toString() || '');
      }
      rows.push(row.join(','));
    }

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `${plot_data.measured_value}_boxplot_${date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const plotContent = (
    <>
      {/* Controls Toolbar */}
      <Box sx={{ mb: 2 }}>
        <PlotControlsToolbar
          onFullscreen={handleFullscreen}
          onResetZoom={handleResetZoom}
          onToggleGrid={handleToggleGrid}
          onToggleLegend={handleToggleLegend}
          onCopyData={handleCopyData}
          onDownloadCSV={handleDownloadCSV}
          gridVisible={gridVisible}
          legendVisible={legendVisible}
        />
      </Box>

      {/* Plot */}
      <Box
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${colors.neutral[200]}`,
        }}
      >
        <Plot
          data={data_for_plot}
          useResizeHandler={true}
          style={{ width: '100%', height: 800 }}
          layout={{
            ...layout,
            yaxis: {
              ...layout.yaxis,
              title: plot_data.measured_value,
              showgrid: gridVisible,
            },
            xaxis: {
              ...layout.xaxis,
              showgrid: gridVisible,
            },
            showlegend: legendVisible,
          }}
          config={plotlyConfig}
          onRelayout={handleRelayout}
        />
      </Box>
    </>
  );

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${colors.neutral[200]}`,
        }}
      >
        <BubbleChartIcon sx={{ color: colors.primary.main, fontSize: '1.5rem' }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          {plot_data.measured_value}
        </Typography>
      </Box>

      {plotContent}

      {/* Fullscreen Modal */}
      <PlotFullscreenModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        title={plot_data.measured_value}
      >
        {plotContent}
      </PlotFullscreenModal>
    </Box>
  );
};
